import React, {
	useEffect, useMemo, useState,
} from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GreenroomState } from '../../stores/types/types';
import { ROOM_STATES } from '../../stores/reducers/settings';
import type { Shot } from '../../stores/types/shots';
import { SHOT_THUMBNAIL_TYPES } from '../../stores/types/shots';
import { FEED_TYPES } from '../../stores/reducers/shots';
import Socket from '../../connection/socket';
import { ConnectedUser } from '../../stores/types/connection';
import { durationString } from '../../utils/utils';
import Icon, { ICONS, COLORS } from '../general-components/icon';
import ShotThumbnail from '../general-components/shot-thumbnail';
import useShots from '../../contexts/shots/provider';
import AvatarBubbleRow from '../general-components/avatar-bubble-row';
import V_LAYOUTS from '../../utils/video-layout';
import type { Profile } from '../../stores/types/invitations';
import Avatar from '../general-components/avatar';
import { useOpenShotModal } from '../../hooks/use-shot-utilities';
import TooltipWrapper from '../general-components/tooltips/tooltip-wrapper';

interface Props {
	open?: boolean;
	playState: number;
	beingDragged: number | undefined;
	isScrollingPinned: boolean;
	shot: Shot;
	onSelect?: (shotId: number) => void;
	onPlay?: (shotId: number) => void;
	onTooltipHover?: (
		hover: boolean,
		missingFeeds: string,
		elRef: HTMLButtonElement | HTMLDivElement
	) => void;
	onDragWhilePinned: () => void;
	shiftKeyDepressed: boolean;
	drawerRef: React.MutableRefObject<HTMLDivElement | null>;
	socket: Socket | null;
}

export default function ShowboardRowThumbnail(props: Props) {
	const {
		open = false,
		playState,
		beingDragged,
		isScrollingPinned,
		shot,
		onSelect,
		onPlay,
		onTooltipHover,
		onDragWhilePinned,
		shiftKeyDepressed,
		drawerRef,
		socket,
	} = props;

	const {
		shot_id: shotId,
		shot_data: {
			forcedLayout,
			shotTitle,
			shotFeeds,
			shotDuration,
			order,
			autoplay,
			autoDuration,
		},
	} = shot;

	const host = useSelector((state: GreenroomState) => state.SessionReducer.host);
	const connectedUsers = useSelector(
		(state: GreenroomState) => state.SessionReducer.connectedUsers,
	);
	const session = useSelector((state: GreenroomState) => state.SessionReducer.session);
	const roomState = useSelector((state: GreenroomState) => state.MeetingReducer.roomState);
	const shots = useSelector((state: GreenroomState) => state.ShotsReducer.shots);
	const shotStartTimestampEntity = useSelector(
		(state: GreenroomState) => state.ShotsReducer.shotStartTimestampEntity?.entityValue?.start,
	);
	const media = useSelector((state: GreenroomState) => state.MediaReducer.media);

	const {
		currentShot,
		changeShotTitle,
		getMissingFeeds,
		hiddenTwoUpUserProfile,
		getShotDuration,
		handleSaveOnEnter,
	} = useShots();

	const handleOpenShotModal = useOpenShotModal();

	const [editShotTitle, setEditShotTitle] = useState<string>(shotTitle);
	const [draggedOut, setDraggedOut] = useState<boolean>(false);
	const [isTransitioning, setIsTransitioning] = useState<boolean>(false);
	const [duration, setDuration] = useState<number>(0);

	const {
		attributes, listeners, setNodeRef, transform, transition, over,
	} = useSortable({
		id: String(shotId),
		transition: { duration: 200, easing: 'cubic-bezier(0.25, 1, 0.5, 1)' },
	});

	function checkTransition() {
		if (!over || draggedOut || isTransitioning) {
			return '';
		}
		if (beingDragged === shot.shot_id) setIsTransitioning(true);
		return transition || '';
	}

	// transition animations cause issues when dragging over the last item in a scrollable container.
	// this is a quirk found in all drag and drop libraries and can even be seen in their examples.
	const style = {
		transform: CSS.Transform.toString(transform),
		transition: checkTransition(),
	};

	const onEditClicked = () => {
		if (socket) {
			handleOpenShotModal(shot, socket);
		}
	};

	useEffect(() => {
		setEditShotTitle(shotTitle);
	}, [shotTitle]);

	useEffect(() => {
		if (!over) {
			setDraggedOut(true);
		} else {
			setDraggedOut(false);
		}
	}, [over]);

	useEffect(() => {
		if (isTransitioning) { setTimeout(() => setIsTransitioning(false), 250); }
	}, [isTransitioning]);

	const missingFeeds = useMemo(() => getMissingFeeds(shotFeeds), [shotFeeds, connectedUsers]);

	const dragEllipses = useMemo(() => {
		if (host && shots.length > 1 && !isScrollingPinned) {
			return (
				<div
					className="drag-ellipses active"
					{...attributes}
					{...listeners}
					id={String(shotId)}
					role="button">
					<Icon name={ICONS.SHOWBOARD_ELLIPSES} color={COLORS.DARK_GRAY} size={12} />
					<span>{order + 1}</span>
				</div>
			);
		} if (host && shots.length > 1) {
			return (
				<div className="drag-ellipses active" onMouseDown={onDragWhilePinned}>
					<Icon name={ICONS.SHOWBOARD_ELLIPSES} color={COLORS.DARK_GRAY} size={12} />
					<span>{order + 1}</span>
				</div>
			);
		}
		return (
				<div className="drag-ellipses">
					<Icon name={ICONS.SHOWBOARD_ELLIPSES} color={COLORS.DARK_GRAY} size={12} />
					<span>{order + 1}</span>
				</div>
		);
	}, [host, shot, shots, isScrollingPinned, attributes, listeners, shotId]);

	const usersViewingShot = useMemo(
		() => connectedUsers.filter(
			(user: ConnectedUser) => user.selectedShowbuilderShot === shotId
			&& user.session !== session && user.in_room,
		),
		[connectedUsers],
	);

	const hiddenTwoUpShot = useMemo(() => shotFeeds.find(
		(feed) => feed.position === 0 && forcedLayout === V_LAYOUTS.TWO.FULL_TWO,
	), [forcedLayout, shotFeeds]);

	const hiddenMediaItem = useMemo(() => media.find(
		(mediaItem) => mediaItem.id && mediaItem.id === hiddenTwoUpShot?.media,
	), [hiddenTwoUpShot, media]);

	const isHiddenTwoUpLayout = useMemo(
		(): boolean => shot?.shot_data?.forcedLayout === V_LAYOUTS.TWO.FULL_TWO,
		[shot],
	);

	const hiddenTwoUpProfile = useMemo(
		(): Profile | null => hiddenTwoUpUserProfile(shotFeeds),
		[connectedUsers, shotFeeds],
	);

	const twoUpAudioMessage = useMemo((): string => {
		const audioString = 'Audio';

		if (hiddenTwoUpProfile) {
			const {
				first_name: firstName,
				last_name: lastName,
				email,
			} = hiddenTwoUpProfile;

			if (firstName || lastName) {
				const name = [firstName, lastName].filter((namePart) => namePart).join(' ');

				return `${name}'s ${audioString}`;
			}

			if (email) {
				return `${email}'s ${audioString}`;
			}
		}

		if (hiddenTwoUpShot?.feedType === FEED_TYPES.MEDIA) {
			return `${hiddenMediaItem?.original_name ?? 'Hidden Media Item'}'s ${audioString}`;
		}

		return 'Add audio feed';
	}, [hiddenMediaItem, hiddenTwoUpProfile]);

	const hiddenFeedImage = useMemo((): JSX.Element => {
		if (hiddenTwoUpProfile) {
			return (
				<Avatar
					user={hiddenTwoUpProfile}
					noBorder={true}
					size={'twentyfour'}
				/>
			);
		}

		if (hiddenTwoUpShot?.feedType === FEED_TYPES.MEDIA) {
			const mediaItemImagesThumb = hiddenMediaItem?.media_data.thumbnail_map[
				hiddenMediaItem.media_data.image_array[0]
			];

			if (hiddenMediaItem && mediaItemImagesThumb) {
				return (
					<div className="shot-thumbnail-bubble avatar twentyfour no-border">
						<img
							className="shot-thumbnail-bubble__image"
							src={mediaItemImagesThumb}
							alt={hiddenMediaItem.original_name ?? 'Hidden Media Item'}
						/>
					</div>
				);
			}

			return (
				<Icon
					name={ICONS.TAB_MEDIA}
					color={COLORS.GRAY_PRESENTER}
					size={16}
				/>
			);
		}

		return (
			<Icon
				name={ICONS.ADD_INVITE}
				color={COLORS.GRAY_PRESENTER}
				size={16}
			/>
		);
	}, [hiddenMediaItem, hiddenTwoUpProfile]);

	useEffect(() => {
		const getAutoDuration = async (feed: number) => {
			setDuration(await getShotDuration(feed));
		};
		if (autoDuration && shot.shot_data.shotFeeds[0].media) {
			getAutoDuration(shot.shot_data.shotFeeds[0].media);
		} else {
			setDuration(shotDuration);
		}
	}, [autoDuration, shot, shotDuration, media, shots]);

	const displayedShotTitle = useMemo((): string => {
		if (editShotTitle) {
			return editShotTitle;
		} else if (host) {
			return 'Unnamed Shot';
		} else {
			return '';
		}
	}, [editShotTitle, host]);

	return (
		<div
			className={classNames('shot-thumbnail-wrapper', {
				dragged: beingDragged === shotId,
				over,
				'between-margin-right': roomState === ROOM_STATES.EDITING && open,
			})}
			ref={setNodeRef}
			style={style}
		>
			{roomState === ROOM_STATES.SHOWTIME && isHiddenTwoUpLayout && (
				<TooltipWrapper message={twoUpAudioMessage}>
					<div className="avatar-image-container">
						<div className="round-green-outline">
							{hiddenFeedImage}
						</div>
					</div>
				</TooltipWrapper>
			)}
			{roomState === ROOM_STATES.SHOWTIME && missingFeeds && (
				<TooltipWrapper message={missingFeeds}>
					<div className="missing-feeds">
						<Icon name={ICONS.ERROR} color={COLORS.WHITE} size={10} />
					</div>
				</TooltipWrapper>
			)}
			<ShotThumbnail
				key={shotId}
				open={open}
				selected={currentShot?.shot_id === shot.shot_id}
				playStart={shotStartTimestampEntity}
				playState={playState}
				isDragging={!!beingDragged}
				borderRatio={open ? 0.25 : 0.1}
				shot={shot}
				onSelect={onSelect}
				onPlay={onPlay}
				onEditClicked={onEditClicked}
				shiftKeyDepressed={shiftKeyDepressed}
				missingFeeds={missingFeeds}
				onTooltipHover={onTooltipHover}
				shotThumbnailType={SHOT_THUMBNAIL_TYPES.SHOWBOARD}
				socket={socket}
			/>
			{open && (
				<div className="shot-thumbnail-below">
					{dragEllipses}
					<div className="shot-meta">
						{roomState === ROOM_STATES.EDITING && host
							? <input
								className="shot-title"
								onBlur={() => changeShotTitle(editShotTitle, shotId)}
								onKeyUp={handleSaveOnEnter}
								placeholder={'Enter Shot Title...'}
								onChange={(e) => setEditShotTitle(e.target.value)}
								value={editShotTitle}
							/>
							: <span className={classNames('shot-title', !editShotTitle && 'untitled')}>
								{displayedShotTitle}
							</span>
						}
						<div className='flex-align-center'>
							<span className="shot-duration">
								{duration ? durationString(duration) : ' '}
							</span>
							<div style={{ display: autoplay ? 'block' : 'none' }}>
								<Icon
									name={ICONS.SHOT_AUTOPLAY_AUTO}
									size={12}
									color={COLORS.WHITE}
								/>
							</div>
						</div>
					</div>
					{roomState === ROOM_STATES.EDITING && usersViewingShot.length > 0 && (
						<AvatarBubbleRow
							invites={usersViewingShot}
							size={'twentyfour'}
							alignEnd={true}
							expandable={false}
						/>
					)}
				</div>
			)}
		</div >
	);
}
